import { useCallback, useRef } from 'react';
import { isDesktopApp, getElectronAPI } from '@/desktop/electronApi';
import {
  getActiveScaleSettings,
  useScaleStore,
  type ScaleSettings,
} from '@/store/scaleStore';
import {
  NCI_POLL_BYTES,
  parseScaleChunk,
  type ScaleProtocol,
  type ScaleReading,
} from '@/utils/scaleProtocols';

type SerialPortLike = {
  open: (opts: Record<string, unknown>) => Promise<void>;
  close: () => Promise<void>;
  readable: ReadableStream<Uint8Array> | null;
  writable: WritableStream<Uint8Array> | null;
  getInfo: () => { usbVendorId?: number; usbProductId?: number };
};

let webPort: SerialPortLike | null = null;
let webReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
let webWriter: WritableStreamDefaultWriter<Uint8Array> | null = null;
let webReadLoop = false;
let webPollTimer: ReturnType<typeof setInterval> | null = null;
let webFrameBuffer = '';
let electronUnsub: (() => void) | null = null;

function webSerialAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'serial' in navigator;
}

function portIdFromInfo(info: { usbVendorId?: number; usbProductId?: number }, i: number) {
  if (info.usbVendorId != null) {
    return `usb-${info.usbVendorId}-${info.usbProductId ?? 0}`;
  }
  return `serial-${i}`;
}

function shouldAccept(reading: ScaleReading, stableOnly: boolean): boolean {
  if (!stableOnly) return true;
  if (reading.stable === false) return false;
  return true;
}

function emitReading(reading: ScaleReading, stableOnly: boolean, cb?: (r: ScaleReading) => void) {
  if (!shouldAccept(reading, stableOnly)) return;
  useScaleStore.getState().setLastWeightKg(reading.kg);
  cb?.(reading);
}

async function stopWebSerial() {
  webReadLoop = false;
  if (webPollTimer) {
    clearInterval(webPollTimer);
    webPollTimer = null;
  }
  try {
    webWriter?.releaseLock();
  } catch {
    /* ignore */
  }
  webWriter = null;
  try {
    await webReader?.cancel();
  } catch {
    /* ignore */
  }
  webReader = null;
  if (webPort) {
    try {
      await webPort.close();
    } catch {
      /* ignore */
    }
  }
  webPort = null;
  webFrameBuffer = '';
}

async function findWebPort(settings: ScaleSettings): Promise<SerialPortLike | null> {
  const serial = (navigator as unknown as { serial: { getPorts: () => Promise<SerialPortLike[]> } }).serial;
  const all = await serial.getPorts();
  if (settings.portId) {
    const matched = all.find((p, i) => portIdFromInfo(p.getInfo(), i) === settings.portId);
    if (matched) return matched;
  }
  if (all.length === 1) return all[0];
  return null;
}

async function startWebSerial(
  settings: ScaleSettings,
  onReading?: (r: ScaleReading) => void,
): Promise<{ ok: boolean; error?: string }> {
  if (!webSerialAvailable()) {
    return { ok: false, error: 'Web Serial needs Chrome or Edge over HTTPS (or localhost).' };
  }
  if (settings.protocol === 'keyboard_wedge') {
    useScaleStore.getState().setStatus('connected');
    useScaleStore.getState().setError(null);
    return { ok: true };
  }

  await stopWebSerial();
  useScaleStore.getState().setStatus('connecting');
  useScaleStore.getState().setError(null);

  let port = await findWebPort(settings);
  if (!port) {
    try {
      const serial = (navigator as unknown as {
        serial: { requestPort: () => Promise<SerialPortLike> };
      }).serial;
      port = await serial.requestPort();
      const info = port.getInfo();
      const id = portIdFromInfo(info, 0);
      useScaleStore.setState((s) => ({
        draft: { ...s.draft, portId: id },
        settings: { ...s.settings, portId: id },
      }));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Port selection cancelled.';
      useScaleStore.getState().setStatus('error');
      useScaleStore.getState().setError(msg);
      return { ok: false, error: msg };
    }
  }

  try {
    await port.open({
      baudRate: settings.baudRate,
      dataBits: settings.dataBits,
      stopBits: settings.stopBits,
      parity: settings.parity,
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Could not open serial port.';
    useScaleStore.getState().setStatus('error');
    useScaleStore.getState().setError(msg);
    return { ok: false, error: msg };
  }

  webPort = port;
  webFrameBuffer = '';
  webReadLoop = true;

  if (port.readable) {
    webReader = port.readable.getReader();
    const decoder = new TextDecoder();
    const protocol = settings.protocol;
    const stableOnly = settings.stableOnly;

    (async () => {
      while (webReadLoop && webReader) {
        try {
          const { value, done } = await webReader.read();
          if (done) break;
          if (!value) continue;
          const chunk = decoder.decode(value, { stream: true });
          const parsed = parseScaleChunk(protocol, chunk, webFrameBuffer);
          webFrameBuffer = parsed.frameBuffer;
          if (parsed.reading) emitReading(parsed.reading, stableOnly, onReading);
        } catch {
          break;
        }
      }
    })();
  }

  if (settings.protocol === 'nci' && port.writable) {
    webWriter = port.writable.getWriter();
    const writer = webWriter;
    webPollTimer = setInterval(() => {
      writer.write(NCI_POLL_BYTES).catch(() => undefined);
    }, 400);
  }

  useScaleStore.getState().setStatus('connected');
  return { ok: true };
}

async function startElectron(
  settings: ScaleSettings,
  onReading?: (r: ScaleReading) => void,
): Promise<{ ok: boolean; error?: string }> {
  const api = getElectronAPI();
  if (!api?.hardware?.scaleStart) {
    return { ok: false, error: 'Scale hardware API unavailable.' };
  }
  if (settings.protocol === 'keyboard_wedge') {
    useScaleStore.getState().setStatus('connected');
    return { ok: true };
  }
  if (!settings.path) {
    return { ok: false, error: 'Select a serial port in Scale settings.' };
  }

  useScaleStore.getState().setStatus('connecting');
  electronUnsub?.();
  electronUnsub =
    api.hardware.onScaleWeight?.((kg: number) => {
      emitReading({ kg }, settings.stableOnly, onReading);
    }) || null;

  // Electron may pass richer reading via IPC later; for now kg number
  const res = await api.hardware.scaleStart({
    path: settings.path,
    baudRate: settings.baudRate,
    protocol: settings.protocol,
    dataBits: settings.dataBits,
    stopBits: settings.stopBits,
    parity: settings.parity,
    stableOnly: settings.stableOnly,
  } as never);

  if (!res?.ok) {
    electronUnsub?.();
    electronUnsub = null;
    useScaleStore.getState().setStatus('error');
    useScaleStore.getState().setError(res?.error || 'Scale start failed.');
    return { ok: false, error: res?.error || 'Scale start failed.' };
  }
  useScaleStore.getState().setStatus('connected');
  useScaleStore.getState().setError(null);
  return { ok: true };
}

export async function startScaleReading(
  onReading?: (r: ScaleReading) => void,
  settingsOverride?: ScaleSettings,
): Promise<{ ok: boolean; error?: string }> {
  const settings = settingsOverride || getActiveScaleSettings();
  if (isDesktopApp()) return startElectron(settings, onReading);
  return startWebSerial(settings, onReading);
}

export async function stopScaleReading(): Promise<void> {
  if (isDesktopApp()) {
    electronUnsub?.();
    electronUnsub = null;
    try {
      await getElectronAPI()?.hardware?.scaleStop?.();
    } catch {
      /* ignore */
    }
  } else {
    await stopWebSerial();
  }
  useScaleStore.getState().setStatus('idle');
}

export function isScaleTransportAvailable(): {
  available: boolean;
  mode: 'electron' | 'web-serial' | 'none';
  note: string;
} {
  if (isDesktopApp()) {
    return {
      available: true,
      mode: 'electron',
      note: 'Desktop app serial ports',
    };
  }
  if (webSerialAvailable()) {
    return {
      available: true,
      mode: 'web-serial',
      note: 'Chrome / Edge Web Serial (HTTPS or localhost)',
    };
  }
  return {
    available: false,
    mode: 'none',
    note: 'Live scale needs Chrome/Edge with Web Serial, or the desktop app. Manual weight entry still works.',
  };
}

export function useScaleConnection() {
  const {
    draft,
    status,
    setStatus,
    setError,
    addPort,
  } = useScaleStore();
  const readingCb = useRef<((r: ScaleReading) => void) | null>(null);

  const listPorts = useCallback(async () => {
    if (isDesktopApp()) {
      const ports = (await getElectronAPI()?.hardware?.scaleListPorts?.()) || [];
      return ports.map((p: { path: string; manufacturer?: string }) => ({
        id: p.path,
        label: p.manufacturer ? `${p.path} (${p.manufacturer})` : p.path,
        path: p.path,
      }));
    }
    if (!webSerialAvailable()) return [];
    const serial = (navigator as unknown as { serial: { getPorts: () => Promise<SerialPortLike[]> } }).serial;
    const all = await serial.getPorts();
    return all.map((p, i) => {
      const info = p.getInfo();
      const id = portIdFromInfo(info, i);
      const label =
        info.usbVendorId != null
          ? `USB ${info.usbVendorId.toString(16)}:${(info.usbProductId ?? 0).toString(16)}`
          : `Serial port ${i + 1}`;
      return { id, label, usbVendorId: info.usbVendorId, usbProductId: info.usbProductId };
    });
  }, []);

  const requestWebPort = useCallback(async () => {
    if (!webSerialAvailable()) {
      setError('Web Serial not available in this browser.');
      return null;
    }
    try {
      const serial = (navigator as unknown as {
        serial: { requestPort: () => Promise<SerialPortLike> };
      }).serial;
      const port = await serial.requestPort();
      const info = port.getInfo();
      const id = portIdFromInfo(info, 0);
      const label =
        info.usbVendorId != null
          ? `USB ${info.usbVendorId.toString(16)}:${(info.usbProductId ?? 0).toString(16)}`
          : 'Serial port';
      const saved = { id, label, usbVendorId: info.usbVendorId, usbProductId: info.usbProductId };
      addPort(saved);
      useScaleStore.getState().updateDraft('portId', id);
      setError(null);
      return saved;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Port selection cancelled.');
      return null;
    }
  }, [addPort, setError]);

  const connect = useCallback(
    async (onReading?: (r: ScaleReading) => void) => {
      readingCb.current = onReading || null;
      const res = await startScaleReading(
        (r) => readingCb.current?.(r),
        useScaleStore.getState().draft,
      );
      return res.ok;
    },
    [],
  );

  const disconnect = useCallback(async () => {
    readingCb.current = null;
    await stopScaleReading();
    setStatus('idle');
    setError(null);
  }, [setStatus, setError]);

  const testConnection = useCallback(async () => {
    const ok = await connect();
    if (!ok) {
      const msg = useScaleStore.getState().errorMsg;
      return { ok: false, error: msg || 'Connection failed.' };
    }
    // Keep connection open so settings page can show live reading
    return { ok: true };
  }, [connect]);

  return {
    status,
    draft,
    listPorts,
    requestWebPort,
    connect,
    disconnect,
    testConnection,
    transport: isScaleTransportAvailable(),
  };
}

export type { ScaleProtocol, ScaleReading };
